import getCustomerLocation from './get-customer-location.js';

(() => {
  const SELECTORS = {
    MODAL: '.js-locale-modal',
    CLOSE_BUTTON: '.locale-modal__close',
    OVERLAY: '.modal-overlay',
    OPEN_BUTTON: '.js-locale-modal-button',
    DISCLOSURE_GROUP: '[data-disclosure-group]',
    DISCLOSURE_BUTTON: '.disclosure__button',
    DISCLOSURE_LIST: '.disclosure__list-wrapper',
    DISCLOSURE_SEARCH: '.disclosure__search-input',
    DISCLOSURE_ITEMS: '.disclosure__item',
    DISCLOSURE_LINKS: '.disclosure__link',
    HIDDEN_INPUT: 'input[type="hidden"]',
    LOCALE_FORM: '.locale-modal__form',
    LANGUAGE_LINKS: '.locale-selector__lang[data-lang]'
  };

  const KEYS = {
    ESCAPE: 'Escape'
  };

  const COOKIE_NAMES = {
    COUNTRY: 'localization',
  };

  // Cookie utilities
  const Cookies = {
    set(name, value, days = 365) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
      console.log(`Setting cookie ${name}=${value}`);
    },
    
    get(name) {
      const cookieName = `${name}=`;
      const cookies = document.cookie.split(';');
      
      for (let i = 0; i < cookies.length; i += 1) {
        const cookie = cookies[i].trim();
        if (cookie.indexOf(cookieName) === 0) {
          const value = cookie.substring(cookieName.length, cookie.length);
          console.log(`Getting cookie ${name}=${value}`);
          return value;
        }
      }
      console.log(`Cookie ${name} not found`);
      return null;
    }
  };

  // Make getCustomerLocation optional
  const tryGetCustomerLocation = async () => {
    try {
      if (typeof getCustomerLocation === 'function') {
        return await getCustomerLocation();
      }
    } catch (error) {
      console.warn('Customer location detection unavailable:', error);
    }
    return null;
  };

  class DisclosureGroup {
    constructor(container) {
      this.container = container;
      this.button = container.querySelector(SELECTORS.DISCLOSURE_BUTTON);
      this.panel = container.querySelector(SELECTORS.DISCLOSURE_LIST);
      this.searchInput = container.querySelector(SELECTORS.DISCLOSURE_SEARCH);
      this.items = container.querySelectorAll(SELECTORS.DISCLOSURE_ITEMS);
      this.links = container.querySelectorAll(SELECTORS.DISCLOSURE_LINKS);
      this.hiddenInput = container.querySelector(SELECTORS.HIDDEN_INPUT);

      this.boundHandleSearch = this.handleSearch.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundOnKeyUp = this.onKeyUp.bind(this);

      // Initialize panel state
      if (this.panel) {
        this.panel.setAttribute('data-visible', 'false');
        this.button?.setAttribute('aria-expanded', 'false');
      }

      this.initializeEventListeners();
    }

    initializeEventListeners() {
      try {
        // Remove any existing click listeners first to avoid duplicates
        if (this.button) {
          // Clear previous listeners by cloning the button
          const newButton = this.button.cloneNode(true);
          this.button.parentNode.replaceChild(newButton, this.button);
          this.button = newButton;
          
          // Add a new click handler with debounce to prevent duplicate fires
          let isProcessing = false;
          this.button.addEventListener('click', (event) => {
            if (isProcessing) return;
            isProcessing = true;
            
            event.preventDefault();
            event.stopPropagation();
            
            const isExpanded = this.button.getAttribute('aria-expanded') === 'true';
            
            // Toggle the state
            this.button.setAttribute('aria-expanded', (!isExpanded).toString());
            console.log('isExpanded', isExpanded);
            
            if (isExpanded) {
              this.hideList();
            } else {
              this.showList();
            }
            
            // Reset after a short delay
            setTimeout(() => {
              isProcessing = false;
            }, 100);
          });
        }
        
        this.searchInput?.addEventListener('input', this.boundHandleSearch);
        this.links?.forEach(link => {
          link.addEventListener('click', this.onLinkClick.bind(this));
        });
        
        // Use capture phase to ensure our handler runs first
        document.addEventListener('click', (event) => {
          // Only proceed if not clicking on our disclosure elements
          if (!this.container.contains(event.target) && !this.button?.contains(event.target)) {
            this.hideList();
          }
        }, true);
        
        document.addEventListener('keyup', this.boundOnKeyUp);
      } catch (error) {
        console.error('Error initializing event listeners:', error);
      }
    }

    destroy() {
      this.searchInput?.removeEventListener('input', this.boundHandleSearch);
      document.removeEventListener('click', this.boundOnDocumentClick);
      document.removeEventListener('keyup', this.boundOnKeyUp);
    }

    toggleList() {
      // This method is now deprecated, using inline handler instead
      return this.button !== null;
    }

    hideList() {
      this.button?.setAttribute('aria-expanded', 'false');
      this.panel?.setAttribute('data-visible', 'false');
      this.panel?.toggleAttribute('hidden', '');
      this.resetSearch();
    }

    resetSearch() {
      if (this.searchInput) {
        this.searchInput.value = '';
        this.showAllItems();
      }
    }

    handleSearch(event) {
      const searchTerm = event.target.value.toLowerCase().trim();
      
      this.items?.forEach((item, index) => {
        const text = item.getAttribute('data-search-term') || '';
        const matches = text.includes(searchTerm);
        
        if (matches) {
          item.classList.remove('hidden');
          // Reset animation delay for visible items
          item.setAttribute('style', `transition-delay: ${0.05 + (index * 0.05)}s`);
        } else {
          item.classList.add('hidden');
          item.setAttribute('style', 'transition-delay: 0s');
        }
      });
    }

    showAllItems() {
      this.items?.forEach(item => {
        item.classList.remove('hidden');
      });
    }

    onDocumentClick(event) {
      if (!this.container.contains(event.target) && !this.button?.contains(event.target)) {
        this.hideList();
      }
    }

    onLinkClick(event) {
      event.preventDefault();
      const { value: selectedValue } = event.currentTarget.dataset;
      const buttonText = event.currentTarget.textContent.trim();
      
      if (selectedValue && this.hiddenInput) {
        this.hiddenInput.value = selectedValue;
        const svgIcon = this.button.querySelector('svg')?.outerHTML || '';
        this.button.innerHTML = `${buttonText}${svgIcon}`;
      }

      this.hideList();
    }

    onKeyUp(event) {
      if (event.code === KEYS.ESCAPE) {
        this.hideList();
      }
    }

    // Set the selected value programmatically
    setSelectedValue(value) {
      if (!value || !this.hiddenInput) return false;
      
      const matchingLink = Array.from(this.links).find(link => 
        link.dataset.value === value
      );
      
      if (matchingLink) {
        this.hiddenInput.value = value;
        const buttonText = matchingLink.textContent.trim();
        const svgIcon = this.button.querySelector('svg')?.outerHTML || '';
        this.button.innerHTML = `${buttonText}${svgIcon}`;
        return true;
      }
      
      return false;
    }

    showList() {
      this.panel?.setAttribute('data-visible', 'true');
      // Small delay before focusing to ensure animation has started
      setTimeout(() => {
        this.searchInput?.focus();
      }, 50);
    }
  }

  const initLocaleModal = async () => {
    console.log('Initializing locale modal...');
    
    const modal = document.querySelector(SELECTORS.MODAL);
    if (!modal) {
      console.error('Locale modal not found in DOM');
      return;
    }
    console.log('Found modal element:', modal);
    
    const closeButton = modal.querySelector(SELECTORS.CLOSE_BUTTON);
    console.log('Found close button:', closeButton);
    
    const openButtons = document.querySelectorAll(SELECTORS.OPEN_BUTTON);
    console.log('Found open buttons:', openButtons.length);
    
    const form = modal.querySelector(SELECTORS.LOCALE_FORM);
    console.log('Found form:', form);

    // Initialize language link handlers
    const initLanguageLinks = () => {
      const languageLinks = document.querySelectorAll(SELECTORS.LANGUAGE_LINKS);
      console.log('Found language links:', languageLinks.length);
      
      languageLinks.forEach(link => {
        link.addEventListener('click', () => {
          // Don't prevent default since we have proper hrefs now
          const targetLang = link.getAttribute('data-lang');
          if (targetLang) {
            console.log('Language link clicked:', targetLang);
          }
        });
      });
    };

    // Initialize language links
    initLanguageLinks();

    // Initialize disclosure groups
    const disclosureGroups = [];
    const disclosureContainers = modal.querySelectorAll(SELECTORS.DISCLOSURE_GROUP);
    console.log('Found disclosure groups:', disclosureContainers.length);

    disclosureContainers.forEach(container => {
      disclosureGroups.push(new DisclosureGroup(container));
    });

    // Find the country and language disclosure groups
    const countryGroup = disclosureGroups.find(group => 
      group.hiddenInput?.name === 'country_code'
    );
    
    const languageGroup = disclosureGroups.find(group => 
      group.hiddenInput?.name === 'language_code'
    );

    // Try to set initial values based on cookies or geolocation
    const initializeLocalePreferences = async () => {
      const savedCountry = Cookies.get(COOKIE_NAMES.COUNTRY);
      
      console.log('Initializing locale preferences:', { savedCountry });
      
      // If we have saved preferences, use those
      let countrySet = false;
      
      if (savedCountry && countryGroup) {
        countrySet = countryGroup.setSelectedValue(savedCountry);
        console.log('Setting country from cookie:', { savedCountry, success: countrySet });
      }
      
      // If no saved preferences, try to detect location
      if (!countrySet && countryGroup) {
        try {
          console.log('Detecting customer location...');
          const customerLocation = await tryGetCustomerLocation();
          console.log('Detected location:', customerLocation);
          
          if (customerLocation?.countryCode && !countrySet) {
            countrySet = countryGroup.setSelectedValue(customerLocation.countryCode);
            console.log('Setting country from detection:', { countryCode: customerLocation.countryCode, success: countrySet });
          }
        } catch (error) {
          console.error('Error getting customer location:', error);
        }
      }
      
      // If still no country set, use the current Shopify country
      if (!countrySet && countryGroup && window.Shopify?.country) {
        countrySet = countryGroup.setSelectedValue(window.Shopify.country);
        console.log('Setting country from Shopify:', { country: window.Shopify.country, success: countrySet });
      }
      
      // For language, use the data attribute from the button
      if (languageGroup) {
        const languageButton = languageGroup.button;
        const currentLanguage = languageButton?.getAttribute('data-current-language');
        if (currentLanguage) {
          const languageSet = languageGroup.setSelectedValue(currentLanguage);
          console.log('Setting language from data attribute:', { currentLanguage, success: languageSet });
        }
      }
    };

    // Save preferences to cookies when form is submitted
    const savePreferences = () => {
      if (countryGroup?.hiddenInput?.value) {
        Cookies.set(COOKIE_NAMES.COUNTRY, countryGroup.hiddenInput.value);
      }
    };

    // Initialize user preferences
    await initializeLocalePreferences();

    // Modal control functions
    const closeModal = () => {
      console.log('Closing modal');
      modal.close();
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      // Hide all disclosure lists when modal closes
      disclosureGroups.forEach(group => group.hideList());
    };

    const openModal = () => {
      console.log('Opening modal');
      try {
        modal.showModal();
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');
        console.log('Modal opened successfully');
      } catch (error) {
        console.error('Error opening modal:', error);
        // Fallback for browsers that don't support showModal
        modal.style.display = 'block';
        modal.setAttribute('open', '');
      }
    };

    // Event Listeners
    closeButton?.addEventListener('click', (e) => {
      console.log('Close button clicked');
      e.preventDefault();
      closeModal();
    });
    
    // Close modal when clicking outside
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) {
        console.log('Clicked outside modal');
        closeModal();
      }
    });
    
    // Add click handlers to all open buttons
    openButtons?.forEach(button => {
      button.addEventListener('click', (e) => {
        // Don't open modal if clicking inside locale-selector__codes
        if (e.target.closest('.locale-selector__codes')) {
          return;
        }
        
        console.log('Open button clicked');
        e.preventDefault();
        openModal();
      });
    });
    
    form?.addEventListener('submit', () => {
      console.log('Form submitted');
      savePreferences();
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === KEYS.ESCAPE && modal.open) {
        console.log('Escape key pressed');
        closeModal();
      }
    });

    // Setup a cleanup function for module unloading if needed
    window.addEventListener('beforeunload', () => {
      disclosureGroups.forEach(group => group.destroy());
    });

    console.log('Locale modal initialization complete');
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    console.log('Document still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', initLocaleModal);
  } else {
    console.log('Document already loaded, initializing immediately');
    initLocaleModal();
  }
})(); 